<template>
  <div class="agreement-card" :class="{ 'agreement-card--expanded': expanded }">
    <div class="agreement-card__header" @click="toggle">
      <div class="agreement-card__header-left">
        <v-icon small class="agreement-card__icon mr-2">fas fa-file-alt</v-icon>
        <div>
          <div class="agreement-card__reference">{{ agreement.reference }}</div>
          <div class="agreement-card__subtitle">
            {{ typeName }} · {{ agreement.dues_qty }} cuotas · {{ formatCurrency(agreement.amount) }}
          </div>
        </div>
      </div>
      <div class="agreement-card__header-right">
        <agreement-status-badge
          v-if="agreement.status"
          :name="agreement.status.name"
          type="agreement" />
        <v-icon
          small
          class="agreement-card__chevron ml-2"
          :class="{ 'agreement-card__chevron--expanded': expanded }">
          fas fa-chevron-down
        </v-icon>
      </div>
    </div>

    <transition name="agreement-expand">
      <div v-show="expanded" class="agreement-card__body">
        <div class="agreement-card__summary-grid">
          <div class="agreement-card__summary-item">
            <span class="agreement-card__summary-label">Monto negociado</span>
            <span class="agreement-card__summary-value">{{ formatCurrency(agreement.amount) }}</span>
          </div>
          <div class="agreement-card__summary-item">
            <span class="agreement-card__summary-label">Pagado</span>
            <span class="agreement-card__summary-value">{{ formatCurrency(summary.total_paid) }}</span>
          </div>
          <div class="agreement-card__summary-item" v-if="isCancelled">
            <span class="agreement-card__summary-label">Cancelado el</span>
            <span class="agreement-card__summary-value">{{ formatDateTime(agreement.cancelled_at) }}</span>
          </div>
          <div class="agreement-card__summary-item" v-else>
            <span class="agreement-card__summary-label">Pendiente</span>
            <span class="agreement-card__summary-value">{{ formatCurrency(summary.total_pending) }}</span>
          </div>
          <div class="agreement-card__summary-item">
            <span class="agreement-card__summary-label">Creado</span>
            <span class="agreement-card__summary-value">{{ formatShortDate(agreement.created_at) }}</span>
          </div>
        </div>

        <agreement-progress-bar
          :agreement="agreement"
          :summary="summary" />

        <agreement-dues-table
          :dues="agreement.dues || []"
          :currency="agreement.currency || 'MXN'" />

        <div v-if="isCancelled" class="agreement-card__cancelled-message">
          <v-icon small color="#A32D2D" class="mr-2">fas fa-exclamation-circle</v-icon>
          <span>Convenio cancelado por incumplimiento de pago</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import AgreementStatusBadge from './AgreementStatusBadge';
import AgreementProgressBar from './AgreementProgressBar';
import AgreementDuesTable from './AgreementDuesTable';

export default {
  components: {
    AgreementStatusBadge,
    AgreementProgressBar,
    AgreementDuesTable,
  },
  props: {
    agreement: {
      type: Object,
      required: true,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    summary() {
      return this.agreement.summary || {
        total_paid: 0,
        total_pending: 0,
        dues_paid: 0,
        dues_pending: 0,
      };
    },
    typeName() {
      return this.agreement.type ? this.agreement.type.name : '-';
    },
    isCancelled() {
      return this.agreement.status && this.agreement.status.name === 'Cancelado';
    },
  },
  methods: {
    toggle() {
      this.$emit('toggle');
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: this.agreement.currency || 'MXN',
      }).format(amount || 0);
    },
    formatShortDate(dateStr) {
      if (!dateStr) {
        return '-';
      }

      return new Date(dateStr).toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
    },
    formatDateTime(dateStr) {
      if (!dateStr) {
        return '-';
      }

      return new Date(dateStr).toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
.agreement-card {
  border: 1px solid #E0DED8;
  border-radius: 8px;
  margin-bottom: 12px;
  background: #fff;
  overflow: hidden;
}

.agreement-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  cursor: pointer;
  user-select: none;
}

.agreement-card__header-left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.agreement-card__icon {
  color: #666663;
  flex-shrink: 0;
}

.agreement-card__reference {
  font-size: 15px;
  font-weight: 600;
  color: #1A1A19;
}

.agreement-card__subtitle {
  font-size: 12px;
  color: #666663;
  margin-top: 2px;
}

.agreement-card__header-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.agreement-card__chevron {
  color: #666663;
  transition: transform 300ms ease;
}

.agreement-card__chevron--expanded {
  transform: rotate(180deg);
}

.agreement-card__body {
  padding: 0 16px 16px;
  border-top: 1px solid #F1EFE8;
}

.agreement-card__summary-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 16px;
}

@media (min-width: 600px) {
  .agreement-card__summary-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.agreement-card__summary-item {
  background: #F7F7F5;
  border-radius: 6px;
  padding: 12px;
}

.agreement-card__summary-label {
  display: block;
  font-size: 11px;
  color: #666663;
  margin-bottom: 4px;
}

.agreement-card__summary-value {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1A1A19;
}

.agreement-card__cancelled-message {
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding: 12px;
  background: #FCEBEB;
  border-radius: 6px;
  color: #791F1F;
  font-size: 13px;
}

.agreement-expand-enter-active,
.agreement-expand-leave-active {
  transition: all 300ms ease;
  overflow: hidden;
}

.agreement-expand-enter,
.agreement-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.agreement-expand-enter-to,
.agreement-expand-leave {
  opacity: 1;
  max-height: 2000px;
}
</style>



// WEBPACK FOOTER //
// src/components/agreements/AgreementCard.vue